import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useRegisterSocketRooms } from '../hooks/useSocketInvalidation';
import { useCallerMe, useCallerAssignments, useCallerRecords, useCallerStats, useSettings } from '../hooks/queries';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Phone, LogOut, CheckCircle, Clock, Users, Search, ChevronLeft, ChevronRight, Edit2, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getStatusColor = (status) => {
  if (!status || status === 'Blank') return { bg: '#F3F4F6', text: '#6B7280' };
  if (status === 'Pledge') return { bg: '#D1FAE5', text: '#065F46' };
  if (status === 'Against') return { bg: '#FEE2E2', text: '#991B1B' };
  if (status === 'Undecided') return { bg: '#FEF3C7', text: '#92400E' };
  return { bg: '#E5E7EB', text: '#4B5563' };
};

export default function CallerDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedBlock, setSelectedBlock] = useState('__all__');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({ pledge_status: '', remarks: '', contact_number: '' });

  const { data: caller, isError: callerError } = useCallerMe();
  const callerId = caller?.caller_id;

  useRegisterSocketRooms(callerId ? [`caller:${callerId}`] : []);

  const { data: assignments = [] } = useCallerAssignments();
  const { data: settings } = useSettings();
  const statusOptions = settings?.pledge_status_options ?? ['Pledge', 'Against', 'Undecided', 'NA', 'WN', 'SO', 'OC'];

  const campaignId = selectedAssignment?.campaign_id;
  const { data: recordsData } = useCallerRecords(campaignId, {
    block: selectedBlock !== '__all__' ? selectedBlock : undefined,
    page: currentPage,
    page_size: 30,
    search: searchTerm || undefined,
  });
  const { data: stats } = useCallerStats(campaignId);

  const records = recordsData?.records ?? [];
  const totalPages = recordsData?.total_pages ?? 0;
  const loading = !caller && !callerError;

  useEffect(() => {
    if (callerError) navigate('/caller/login');
  }, [callerError, navigate]);

  useEffect(() => {
    if (assignments.length > 0 && !selectedAssignment) {
      setSelectedAssignment(assignments[0]);
    }
  }, [assignments, selectedAssignment]);

  const invalidateCaller = () => {
    queryClient.invalidateQueries({ queryKey: ['caller'] });
  };

  const handleLogout = async () => {
    try {
      await api.post(`${API}/callers/logout`, {});
    } finally {
      navigate('/caller/login');
    }
  };

  const openEditDialog = (record) => {
    setEditingRecord(record);
    setEditForm({
      pledge_status: record.pledge_status || '',
      remarks: record.remarks || '',
      contact_number: record.contact_number || ''
    });
    setEditDialogOpen(true);
  };

  const saveRecord = async () => {
    if (!editingRecord) return;
    
    try {
      await axios.put(
        `${API}/caller/records/${editingRecord.record_id}`,
        editForm,
        { withCredentials: true }
      );
      
      toast.success('Record updated');
      setEditDialogOpen(false);
      invalidateCaller();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    }
  };

  const quickStatusUpdate = async (recordId, newStatus) => {
    try {
      await api.put(`${API}/caller/records/${recordId}`, {
        pledge_status: newStatus === 'Blank' ? '' : newStatus
      });
      toast.success('Status updated');
      invalidateCaller();
    } catch (error) {
      toast.error('Update failed');
    }
  };

  const markBlockComplete = async (blockNumber) => {
    if (!selectedAssignment) return;
    
    if (!window.confirm(`Mark block "${blockNumber}" as completed? You won't see these records anymore.`)) return;
    
    try {
      await api.post(
        `${API}/caller/blocks/complete?campaign_id=${selectedAssignment.campaign_id}`,
        { block_number: blockNumber }
      );
      toast.success(`Block ${blockNumber} marked as completed`);
      invalidateCaller();
      setSelectedBlock('__all__');
    } catch (error) {
      toast.error('Failed to mark block as complete');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Phone className="h-6 w-6 text-primary" />
            <div>
              <h1 className="font-bold text-lg">Call Centre</h1>
              <p className="text-sm text-muted-foreground">{caller?.name}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4">
        {assignments.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Blocks Assigned</h3>
              <p className="text-muted-foreground">You don't have any blocks assigned yet. Please contact your administrator.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Campaign Selector */}
            {assignments.length > 1 && (
              <div className="mb-4">
                <Label>Campaign</Label>
                <Select
                  value={selectedAssignment?.campaign_id || ''}
                  onValueChange={(v) => {
                    const assignment = assignments.find(a => a.campaign_id === v);
                    setSelectedAssignment(assignment);
                    setSelectedBlock('__all__');
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-full md:w-80">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {assignments.map(a => (
                      <SelectItem key={a.campaign_id} value={a.campaign_id}>
                        {a.campaign_name} - {a.constituency_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-blue-500" />
                      <div>
                        <p className="text-2xl font-bold">{stats.total_records}</p>
                        <p className="text-xs text-muted-foreground">Total Assigned</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <div>
                        <p className="text-2xl font-bold">{stats.records_with_status}</p>
                        <p className="text-xs text-muted-foreground">Updated</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-amber-500" />
                      <div>
                        <p className="text-2xl font-bold">{stats.pending}</p>
                        <p className="text-xs text-muted-foreground">Blocks Pending</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="text-2xl font-bold">{stats.completed}</p>
                        <p className="text-xs text-muted-foreground">Blocks Done</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Blocks and Search */}
            <div className="flex flex-col md:flex-row gap-3 mb-4">
              <Select value={selectedBlock} onValueChange={(v) => { setSelectedBlock(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="Select Block" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All My Blocks</SelectItem>
                  {selectedAssignment?.active_blocks?.map(block => (
                    <SelectItem key={block} value={block}>Block {block}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, ID, or phone..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  className="pl-10"
                />
              </div>

              {selectedBlock !== '__all__' && (
                <Button variant="outline" onClick={() => markBlockComplete(selectedBlock)}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark Block Complete
                </Button>
              )}
            </div>

            {/* Records List */}
            <div className="space-y-3">
              {records.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-muted-foreground">
                    No records found
                  </CardContent>
                </Card>
              ) : (
                records.map(record => {
                  const statusColors = getStatusColor(record.pledge_status);
                  return (
                    <Card key={record.record_id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="font-semibold">{record.name}</h3>
                                <p className="text-sm text-muted-foreground">{record.address}</p>
                              </div>
                              <span className="text-xs bg-secondary px-2 py-1 rounded">
                                Block {record.block_number}
                              </span>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-4 text-sm">
                              <span><span className="text-muted-foreground">ID:</span> {record.id_number}</span>
                              {record.contact_number && (
                                <a href={`tel:${record.contact_number}`} className="text-blue-600 flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {record.contact_number}
                                </a>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Select
                              value={record.pledge_status || 'Blank'}
                              onValueChange={(v) => quickStatusUpdate(record.record_id, v)}
                            >
                              <SelectTrigger 
                                className="w-32 h-9"
                                style={{ backgroundColor: statusColors.bg, color: statusColors.text }}
                              >
                                <SelectValue>{record.pledge_status || 'Select'}</SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                {statusOptions.map(opt => (
                                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                                <SelectItem value="Blank">Blank</SelectItem>
                              </SelectContent>
                            </Select>
                            
                            <Button variant="outline" size="sm" onClick={() => openEditDialog(record)}>
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {record.remarks && (
                          <p className="mt-2 text-sm text-muted-foreground border-t pt-2">
                            <span className="font-medium">Remarks:</span> {record.remarks}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">Page {currentPage} of {totalPages}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Record</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {editingRecord && (
              <div className="bg-secondary p-3 rounded-lg">
                <p className="font-semibold">{editingRecord.name}</p>
                <p className="text-sm text-muted-foreground">{editingRecord.address}</p>
              </div>
            )}
            
            <div>
              <Label>Pledge Status</Label>
              <Select value={editForm.pledge_status} onValueChange={(v) => setEditForm({ ...editForm, pledge_status: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map(opt => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                  <SelectItem value="">Blank</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Contact Number</Label>
              <Input
                value={editForm.contact_number}
                onChange={(e) => setEditForm({ ...editForm, contact_number: e.target.value })}
                placeholder="Phone number"
              />
              <p className="text-xs text-muted-foreground mt-1">Changes are logged for audit</p>
            </div>
            
            <div>
              <Label>Remarks</Label>
              <Input
                value={editForm.remarks}
                onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })}
                placeholder="Add notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveRecord}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
